import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../adminApi';
import AdminLayout from './AdminLayout';
import { STATUS_MAP } from '../../constants/projectStatus';

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};

const FILTER_TABS = [
    { key: 'all',            label: 'すべて' },
    { key: 'active',         label: '対応中' },
    { key: 'pending_payout', label: '振込待ち' },
    { key: 'completed',      label: '完了' },
    { key: 'lost',           label: '失注' },
];

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const AdminProjectListPage = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => {
        setLoading(true);
        api.get(`admin/projects?filter=${filter}&search=${encodeURIComponent(search)}`)
            .then(r => setProjects(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [filter, search]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    return (
        <AdminLayout>
            <div style={{ padding: '32px' }}>
                {/* ヘッダー */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>案件管理</h2>
                    <span style={{ padding: '3px 12px', background: '#005c53', color: '#fff', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600 }}>
                        {projects.length}件
                    </span>
                </div>

                {/* フィルタータブ */}
                <div style={{ borderBottom: '2px solid #e0e4ea', marginBottom: 20, display: 'flex', gap: 4 }}>
                    {FILTER_TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            style={{
                                padding: '10px 18px', border: 'none', cursor: 'pointer', background: 'none',
                                fontSize: '0.875rem', fontWeight: filter === tab.key ? 700 : 400,
                                color: filter === tab.key ? '#005c53' : '#6b7280',
                                borderBottom: filter === tab.key ? '2px solid #005c53' : '2px solid transparent',
                                marginBottom: -2,
                                transition: 'color 0.15s',
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* 検索バー */}
                <form onSubmit={handleSearch} style={{ marginBottom: 20 }}>
                    <div style={{ position: 'relative', maxWidth: 400 }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>
                            <SearchIcon />
                        </span>
                        <input
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            placeholder="顧客名・業者名・案件IDで検索"
                            style={{
                                width: '100%', paddingLeft: 40, paddingRight: 16, paddingTop: 9, paddingBottom: 9,
                                border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem',
                                outline: 'none', boxSizing: 'border-box',
                            }}
                        />
                    </div>
                </form>

                {/* テーブル */}
                <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                <thead>
                                    <tr style={{ background: '#f5f6f8', borderBottom: '1px solid #e0e4ea' }}>
                                        {['案件ID', '顧客名', '業者名', 'ステータス', '工事代金', '作成日', '操作'].map(h => (
                                            <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: '#6b7280', fontWeight: 500, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                                                該当する案件がありません
                                            </td>
                                        </tr>
                                    ) : projects.map(p => {
                                        const st = STATUS_MAP[p.status] || { label: p.status, bg: '#f3f4f6', color: '#6b7280' };
                                        const customer = p.customer ? `${p.customer.last_name} ${p.customer.first_name}` : '—';
                                        const amount = p.invoice?.amount ? `¥${fmt(p.invoice.amount)}` : '—';
                                        return (
                                            <tr key={p.id} style={{ borderBottom: '1px solid #e0e4ea' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f5f6f8'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 500, whiteSpace: 'nowrap' }}>#{p.id}</td>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', whiteSpace: 'nowrap' }}>{customer}</td>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', whiteSpace: 'nowrap' }}>{p.contractor?.company_name || '—'}</td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: st.bg, color: st.color }}>
                                                        {st.label}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 500, whiteSpace: 'nowrap' }}>{amount}</td>
                                                <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(p.created_at)}</td>
                                                <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => navigate(`/admin/projects/${p.id}`)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#005c53', fontWeight: 600, fontSize: '0.875rem', padding: 0 }}
                                                    >
                                                        詳細
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </AdminLayout>
    );
};

export default AdminProjectListPage;
